import PluginContributions from "*external*/core/utils/PluginContributions";
import { type Contribution } from "*external*/core/utils/PluginContributions";
import serviceRegistry from "*external*/core/utils/serviceRegistry";

// specify which stage of startup to run the callback
// 'premount' is called before shell initializes and should only be used for preloading critical data
type Hook = "premount" | "mounted";

type StartupData = {
   hook?: Hook;
   requiresAuthentication?: boolean;
};

type AllowConfig = {
   pluginIds: string[];
};

const isHook = (hook: Hook) => (cs: Contribution<StartupData>) =>
   (cs.data.hook ?? "mounted") === hook;

const requiresAuth = (authorized: boolean) => (cs: Contribution<StartupData>) =>
   authorized ? true : cs.data.requiresAuthentication === false;

const isFromPlugin =
   (...ids: string[]) =>
   (cs: Contribution) =>
      ids.includes(cs.sourcePluginId);

const isFromAdminConfig = isFromPlugin("cisco.harbor.pui.config");
const isFromAuthPlugin = isFromPlugin("cisco.dna.core.auth");
const isFromShell = isFromPlugin("cisco.dna.core.shell");

// A list of plugins that are "allowed" to startup.
// This is used to prevent any ui-plugin from polluting the init scope,
// which can hurt first time load times
const allowlist = (): string[] => [
   // A list of our dna's core plugins that require startup functionality
   "cisco.dna.core.app",
   "cisco.dna.core.appCloud",
   "cisco.dna.core.theme",
   "cisco.dna.core.displaySettings",
   "cisco.dna.core.platformBanner",
   "cisco.dna.core.featureFlags",
   "cisco.dna.core.hotkeys",
   "cisco.dna.core.analytics",
   "cisco.dna.core.loop",
   "cisco.dna.core.announcements",
   "cisco.dna.core.product",
   "cisco.harbor.platform",
   "cisco.harbor.persistence",
   "cisco.harbor.config",

   // Startup plugins from our org
   "cisco.dna.va.launcher-ui-migrator",

   // [Deprecated]
   // Extended allow list through environment variables, that get passed on from the backend
   ...(window.platformUI.startupAllowedList || []),

   // Allow list can be extened via contributions
   // for product-specific plugin IDs
   ...new PluginContributions("cisco.harbor.pui.startup")
      .forExtensionPoint<AllowConfig>("allowlist")
      .filter(isFromAdminConfig)
      .flatMap((it) => it.data.pluginIds),
];

const startupContributions = () => [
   // For compatibility, we are still supporting the "cisco.dna.core/startup" endpoint
   ...new PluginContributions("cisco.dna.core").forExtensionPoint<StartupData>(
      "startup"
   ),
   ...new PluginContributions(
      "cisco.harbor.pui.startup"
   ).forExtensionPoint<StartupData>("callback"),
];

const isFunction = (fn: unknown) => {
   if (typeof fn !== "function")
      throw new Error(
         `Startup contribution is expected to be a function, but instead got '${typeof fn}'`
      );
   return fn;
};

const runStartup = async (contribution: Contribution<StartupData>) =>
   contribution
      .loadModule()
      .then(isFunction)
      .then((f) => f())
      .catch((e) =>
         console.error(
            `An error occured while running startup function for '${contribution.sourcePluginId}':`,
            e
         )
      );

const runStartupMany = (cs: Contribution<StartupData>[]) =>
   Promise.all(cs.map(runStartup));

const authorizedState = () =>
   serviceRegistry
      .getService("cisco.dna.core.auth", "session")
      .then((session) => session.authorized)
      .catch((_) => false);

// The main loader for DNAC core plugins
export const startup = async (): Promise<void> => {
   const contribs = startupContributions();
   await runStartupMany(contribs.filter(isFromAuthPlugin));

   const authorized = await authorizedState();
   const startup = contribs
      .filter(isFromPlugin(...allowlist()))
      .filter(requiresAuth(authorized));

   await runStartupMany(startup.filter(isHook("premount")));
   await runStartupMany(contribs.filter(isFromShell));
   await runStartupMany(startup.filter(isHook("mounted")));
};
